<template>
  <template v-if="loading">
    <LoadingList :count="3" no-bg />
  </template>
  <template v-else-if="!currentClassification">
    <WdsAlert warning icon class="tw-mb-4">
      <WdsStack direction-column gap-md>
        <WdsText>
          {{ $t('To classify this hire, please start by confirming the contract type.') }}
        </WdsText>
        <WdsButton
          primary
          :href="getContractTypeUrl()"
          class="tw-w-fit"
          data-test="button-confirm-contract-type"
        >
          <WdsStack align-center gap-sm>
            {{ $t('Confirm contract type') }}
            <WdsIcon name="chevron-right" :size="16" />
          </WdsStack>
        </WdsButton>
      </WdsStack>
    </WdsAlert>
  </template>
  <template v-else>
    <WdsAlert v-if="hasDontKnowAnswers && hasSubmittedClassification" icon warning>
      {{
        $t(
          'Some answers are still marked as "Don\'t know". Providing more answers will yield a more accurate result.',
        )
      }}
    </WdsAlert>
    <WdsStack direction-column justify-space-between class="tw-w-full">
      <!-- Questionnaire -->
      <WdsStack direction-column gap-md class="tw-w-full tw-px-4 tw-py-6 tw-border-b">
        <WdsStack align-center justify-space-between class="tw-w-full">
          <WdsStack align-center gap-sm>
            <span
              v-if="
                hasSubmittedClassification ||
                hasPendingAcceptanceClassification ||
                hasAcceptedClassification
              "
              class="tw-bg-green-50 tw-items-center tw-justify-center tw-flex tw-rounded-full tw-h-6 tw-w-6"
            >
              <WdsIcon name="checkmark" :size="15" class="tw-text-green-600" />
            </span>
            <span
              v-else
              class="tw-items-center tw-justify-center tw-flex tw-rounded-full tw-h-6 tw-w-6 tw-bg-indigo-500"
            >
              <span class="tw-text-white tw-font-medium">1</span>
            </span>
            <WdsText large>{{ $t('Complete questionnaire') }}</WdsText>
          </WdsStack>
          <WdsButton
            v-if="
              (hasUnknownAnswers && currentClassification?.hasPendingApproval) ||
              (!hasPendingAcceptanceClassification && !currentClassification?.acceptedStatus)
            "
            :primary="hasUnknownAnswers"
            :href="getEditUrl()"
            data-test="button-complete-questionnaire"
          >
            <WdsStack align-center gap-sm>
              {{ $t('Edit') }}
              <WdsIcon name="chevron-right" :size="16" />
            </WdsStack>
          </WdsButton>
        </WdsStack>
        <WdsStack
          v-if="hasUpdatedAnswers || showsInputStatus"
          class="tw-w-full"
          direction-column
          gap-sm
        >
          <WdsAlert
            v-if="showsInputStatus && allInputReceived"
            icon
            success
            bordered
            :title="$t('All input received')"
            data-test="classification-input-received"
          />
          <WdsCallout
            v-else-if="showsInputStatus"
            warning
            icon="time"
            :toggle-label="$t('Details')"
            :toggle-label-expanded="$t('Hide')"
            class="classification-input-callout"
            data-test="classification-input-status"
          >
            {{ waitingForInputMessage }}
            <template #details>
              <WdsStack direction-column gap-md>
                <WdsStack
                  v-for="row in inputRows"
                  :key="row.key"
                  align-start
                  gap-sm
                  :data-test="`classification-input-${row.key}`"
                >
                  <WdsIcon
                    :name="row.completed ? 'checkmark-filled' : row.icon"
                    :size="16"
                    class="tw-mt-0.5 tw-shrink-0"
                    :class="row.completed ? 'tw-text-green-500' : 'tw-text-gray-600'"
                  />
                  <WdsStack direction-column class="tw-flex-1">
                    <WdsText class="tw-font-medium">{{ row.label }}</WdsText>
                    <WdsText v-if="row.note" sm secondary>{{ row.note }}</WdsText>
                  </WdsStack>
                  <WdsLabel
                    sm
                    subtle
                    class="tw-self-center"
                    :success="row.completed"
                    :warning="!row.completed"
                    :label="row.completed ? $t('Completed') : $t('Pending')"
                  />
                </WdsStack>
              </WdsStack>
            </template>
          </WdsCallout>
          <WdsAlert
            v-if="hasUpdatedAnswers"
            icon
            primary
            bordered
            :title="$t('Worker updated their answers')"
          >
            <template #description>
              {{
                $t(
                  'Please review the changes. You can choose to align the answers with the workers - this may change the classification result.',
                )
              }}
            </template>
            <template #footer>
              <WdsButton :href="getEditUrl()" primary sm>
                {{ $t('Review') }}
              </WdsButton>
            </template>
          </WdsAlert>
        </WdsStack>
      </WdsStack>
      <!-- Result -->
      <WdsStack direction-column class="tw-w-full tw-px-4 tw-py-6 tw-border-b">
        <WdsStack align-center justify-space-between>
          <WdsStack align-center gap-sm>
            <span
              v-if="hasSubmittedClassification"
              class="tw-items-center tw-justify-center tw-flex tw-rounded-full tw-h-6 tw-w-6 tw-bg-indigo-500"
            >
              <span class="tw-text-white tw-font-medium">2</span>
            </span>
            <span
              v-else-if="hasPendingAcceptanceClassification || hasAcceptedClassification"
              class="tw-bg-green-50 tw-items-center tw-justify-center tw-flex tw-rounded-full tw-w-6 tw-h-6"
            >
              <WdsIcon name="checkmark" :size="16" class="tw-text-green-600" />
            </span>
            <span
              v-else
              class="tw-items-center tw-justify-center tw-flex tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-solid tw-border-gray-400"
            >
              <span class="tw-text-gray-600 tw-font-medium">2</span>
            </span>
            <WdsText
              :secondary="
                !hasSubmittedClassification &&
                !hasPendingAcceptanceClassification &&
                !hasAcceptedClassification
              "
              large
            >
              {{ $t('Classification result') }}
            </WdsText>
          </WdsStack>
          <BaseHtmlForm
            v-if="hasSubmittedClassification && !hasResult"
            :action="getCreateUrl()"
            method="POST"
            @submit="handleCreateWCRSubmit"
          >
            <WdsButton
              :primary="!hasUnknownAnswers"
              type="submit"
              :disabled="isCreatingWCR || !canCreateResult"
              data-test="button-create-classification"
            >
              <WdsStack align-center gap-sm>
                <template v-if="isCreatingWCR">
                  <WdsSpinner :size="16" />
                  {{ $t('Creating...') }}
                </template>
                <template v-else>
                  {{ $t('Create :wcrTerm', { wcrTerm: currentClassification?.wcrTerm }) }}
                  <WdsIcon name="chevron-right" :size="16" />
                </template>
              </WdsStack>
            </WdsButton>
          </BaseHtmlForm>
          <WdsTag v-else-if="hasResult && currentClassification?.pdfUrl" primary lg>
            {{ currentClassification?.result?.label }}
          </WdsTag>
          <WdsText v-else class="tw-text-gray-500">
            <WdsButton primary disabled @click="() => {}">
              <WdsStack align-center gap-sm>
                {{ $t('Create :wcrTerm', { wcrTerm: currentClassification?.wcrTerm }) }}
                <WdsIcon name="chevron-right" :size="16" />
              </WdsStack>
            </WdsButton>
          </WdsText>
        </WdsStack>
        <WdsAlert
          v-if="hasSubmittedClassification && !hasResult && !canCreateResult"
          icon
          warning-subtle
          bordered
          class="tw-w-full tw-mt-3"
          :title="$t('Some questions are still unanswered')"
        >
          <template #description>
            {{
              workerCompletedAnswers
                ? $t('The result can be created once every question has an answer.')
                : $t(
                    'The result can be created once every question has an answer. The worker has not completed theirs yet.',
                  )
            }}
          </template>
        </WdsAlert>
        <WdsStack
          v-if="hasResult && currentClassification?.overridesAnother"
          class="tw-w-full tw-flex"
        >
          <WdsAlert info-subtle class="tw-w-full tw-mt-3" :title="$t('Override applied')" icon>
            <template #description>
              <WdsText>
                {{
                  $t('The original classification (Employee W-2) was overridden by :user', {
                    user: currentClassification?.overrideUser?.name,
                  })
                }}
              </WdsText>
              <WdsText class="tw-mt-2 tw-bg-white tw-p-2">
                <strong>{{ $t('Reason:') }}</strong>
                {{ currentClassification?.overrideReason }}
              </WdsText>
            </template>
          </WdsAlert>
        </WdsStack>
      </WdsStack>

      <WdsAlert
        v-if="hasPendingAcceptanceClassification && currentClassification?.hasPendingApproval"
        warning
        class="tw-mt-2"
      >
        <WdsStack direction-row gap-sm justify-space-between>
          <WdsStack gap-sm>
            <WdsIcon name="stamp" :size="19" />
            <div>
              {{ $t('Classification result requires internal approval before acceptance.') }}
            </div>
          </WdsStack>
          <WdsButtonText
            :href="
              laravelNamedRoute('hires.hire.view', {
                hire: hireId,
                tab: 'approvals',
              })
            "
          >
            {{ $t('See details') }}
          </WdsButtonText>
        </WdsStack>
      </WdsAlert>

      <!-- Recommendation -->
      <WdsStack align-center justify-space-between class="tw-w-full tw-px-4 tw-py-6 tw-border-b">
        <WdsStack align-center gap-sm>
          <span
            v-if="canAcceptClassification"
            class="tw-items-center tw-justify-center tw-flex tw-rounded-full tw-h-6 tw-w-6 tw-bg-indigo-500"
          >
            <span class="tw-text-white tw-font-medium">3</span>
          </span>
          <span
            v-else-if="hasAcceptedClassification"
            class="tw-bg-green-50 tw-items-center tw-justify-center tw-flex tw-rounded-full tw-w-6 tw-h-6"
          >
            <WdsIcon name="checkmark" :size="15" class="tw-text-green-600" />
          </span>
          <span
            v-else
            class="tw-items-center tw-justify-center tw-flex tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-solid tw-border-gray-400"
          >
            <span class="tw-text-gray-600 tw-font-medium">3</span>
          </span>
          <WdsText :secondary="!canAcceptClassification && !hasAcceptedClassification" large>
            {{ $t('Finalise recommendation') }}
          </WdsText>
        </WdsStack>
        <WdsButton
          v-if="canAcceptClassification"
          primary
          data-test="button-accept-classification"
          @click="handleAcceptClassification"
        >
          <WdsStack align-center gap-sm>
            <WdsIcon name="checkmark-outline" :size="16" />
            {{ $t('Accept') }}
          </WdsStack>
        </WdsButton>
        <WdsButton
          v-else-if="
            hasPendingAcceptanceClassification &&
            (currentClassification?.hasPendingApproval ||
              currentClassification?.hasHireBlockingCondition)
          "
          primary
          disabled
          @click="handleAcceptClassification"
        >
          <WdsStack align-center gap-sm>
            <WdsIcon name="checkmark-outline" :size="16" />
            {{ $t('Accept') }}
          </WdsStack>
        </WdsButton>
        <WdsStack v-else-if="hasAcceptedClassification" direction-column align-end>
          <span class="tw-text-green-500 tw-font-medium">
            {{ $t(':wcrTerm accepted', { wcrTerm: currentClassification?.wcrTerm }) }}
          </span>
          <span class="tw-text-sm">by {{ currentClassification?.user?.name }}</span>
        </WdsStack>
      </WdsStack>
    </WdsStack>

    <WdsAlert
      v-if="hire?.activeStatus === HireActiveStatus.Draft && hasAcceptedClassification"
      info
      icon
      bordered
      class="tw-mb-3"
    >
      <WdsStack direction-row gap-sm justify-space-between align-center>
        <div>
          {{
            $t(
              'Classification is completed. You can continue with any remaining actions on the overview tab.',
            )
          }}
        </div>
        <WdsButton
          primary
          class="tw-justify-center tw-items-center"
          :href="laravelNamedRoute('hires.hire.view', { hire: hireNumber, tab: 'overview' })"
        >
          {{ $t('Continue') }}
          <WdsIcon name="chevron-right" :size="16" class="tw-ml-1" />
        </WdsButton>
      </WdsStack>
    </WdsAlert>

    <WdsAlert
      v-if="currentClassification?.hasHireBlockingCondition"
      error
      icon
      bordered
      class="tw-mb-3"
    >
      <WdsStack direction-row gap-sm justify-space-between align-center>
        <div>
          {{
            $t(
              'Payroll engagements are restricted by your company. This hire cannot be offered with the current classification result.',
            )
          }}
        </div>
        <WdsButton
          error
          class="tw-justify-center tw-items-center tw-whitespace-nowrap"
          @click="determineCancelAction"
        >
          {{ $t(hire?.canCancelContract ? 'Cancel hire' : 'Terminate hire') }}
        </WdsButton>
      </WdsStack>
    </WdsAlert>

    <!-- Confirmation Modal -->
    <ClassificationConfirmationModal
      v-if="showConfirmationModal && !showOverride && currentClassification"
      :classification="currentClassification"
      :hire-number="hireNumber"
      :csrf-token="csrfToken"
      @close="resetCurrentClassification"
    />
  </template>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { useRoute, useRouter } from 'vue-router'
import {
  WdsAlert,
  WdsButton,
  WdsButtonText,
  WdsCallout,
  WdsIcon,
  WdsLabel,
  WdsSpinner,
  WdsStack,
  WdsTag,
  WdsText,
} from '@worksome/wds-components'
import { useCancelHireModal } from '../composables/useCancelHireModal'
import { useTerminateContractModal } from '../composables/useTerminateContractModal'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { graphql } from '@/gql'
import { ClassificationResult, ClassificationStatus, HireActiveStatus } from '@/gql/graphql'
import ClassificationConfirmationModal from '@/components/Hire/ClassificationConfirmationModal.vue'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'
import { getClassificationEditUrl, getClassificationTypeSlug } from '@/utils/classificationUtils'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'

interface Props {
  hireId: string
}

const props = defineProps<Props>()

const router = useRouter()
const route = useRoute()

// Get CSRF token from meta tag
const csrfToken = computed(() => {
  const metaTag = document.querySelector('meta[name="csrf-token"]')
  return metaTag?.getAttribute('content') || ''
})

// GraphQL Query
const { result, loading, error } = useQuery(
  graphql(`
    query HireClassificationsForSteps($hire: ID!) {
      hire(id: $hire) {
        id
        number
        activeStatus
        canCancelContract
        endClient {
          id
          name
        }
        classification {
          id
          user {
            id
            name
          }
          status
          type
          result {
            outcome
            label
            title
          }
          pdfUrl
          acceptedStatus
          hasUnknownAnswers
          hasDontKnowAnswers
          hasPendingApproval
          hasUpdatedAnswers
          hasWorkerCompletedAnswers
          hasWorkerFields
          hasWorkerCompletedFields
          overridesAnother
          overrideReason
          overrideUser {
            id
            name
          }
          wcrTerm
          hasHireBlockingCondition
          endClientInputComplete
          canCreateResult
        }
      }
    }
  `),
  { hire: props.hireId },
  { clientId: 'publicApi' },
)

const hire = computed(() => result.value?.hire ?? null)

const currentClassification = computed(() => result.value?.hire?.classification ?? null)

const showOverride = ref(false)
const showConfirmationModal = ref(false)
const isCreatingWCR = ref(false)

const hasUnknownAnswers = computed(() => currentClassification.value?.hasUnknownAnswers ?? false)
const hasDontKnowAnswers = computed(
  () => currentClassification.value?.hasDontKnowAnswers ?? false,
)
const hasUpdatedAnswers = computed(() => currentClassification.value?.hasUpdatedAnswers ?? false)
const isSupplierHire = computed(() => hire.value?.endClient !== null && hire.value?.endClient !== undefined)
const endClientInputComplete = computed(
  () => currentClassification.value?.endClientInputComplete ?? true,
)
const canCreateResult = computed(() => currentClassification.value?.canCreateResult ?? false)

const workerCompletedAnswers = computed(
  () => currentClassification.value?.hasWorkerCompletedAnswers ?? false,
)
const workerHasFields = computed(() => currentClassification.value?.hasWorkerFields ?? false)
const workerCompletedFields = computed(
  () => currentClassification.value?.hasWorkerCompletedFields ?? false,
)

const hireNumber = computed(() => result.value?.hire?.number || props.hireId)

const hasResult = computed(
  () =>
    currentClassification.value?.result &&
    currentClassification.value?.result?.outcome !== ClassificationResult.Unknown,
)

const hasSubmittedClassification = computed(
  () => currentClassification.value?.status === ClassificationStatus.Submitted,
)

const hasPendingAcceptanceClassification = computed(
  () => currentClassification.value?.status === ClassificationStatus.PendingAcceptance,
)

const hasAcceptedClassification = computed(
  () =>
    currentClassification.value?.status === ClassificationStatus.Accepted &&
    currentClassification.value?.acceptedStatus,
)

const showsInputStatus = computed(
  () =>
    isSupplierHire.value ||
    (!hasSubmittedClassification.value &&
      !hasPendingAcceptanceClassification.value &&
      !hasAcceptedClassification.value),
)

const allInputReceived = computed(
  () =>
    (!isSupplierHire.value || endClientInputComplete.value) &&
    workerCompletedAnswers.value &&
    (!workerHasFields.value || workerCompletedFields.value),
)

const inputRows = computed(() => {
  const row = (
    key: string,
    icon: 'building' | 'user' | 'list-checked',
    label: string,
    completed: boolean,
    note?: string,
  ) => ({ key, icon, label, completed, note })

  const rows: ReturnType<typeof row>[] = []

  if (isSupplierHire.value) {
    rows.push(
      row(
        'end-client',
        'building',
        $t('End client input'),
        endClientInputComplete.value,
        endClientInputComplete.value
          ? undefined
          : $t('The end client must answer before the classification can proceed'),
      ),
    )
  }

  rows.push(
    row(
      'worker-answers',
      'user',
      $t('Worker answers'),
      workerCompletedAnswers.value,
      workerCompletedAnswers.value
        ? undefined
        : $t('Waiting for the worker to answer the questions'),
    ),
  )

  if (workerHasFields.value) {
    rows.push(
      row(
        'worker-fields',
        'list-checked',
        $t('Additional classification input'),
        workerCompletedFields.value,
        workerCompletedFields.value ? undefined : $t('Extra details requested from the worker'),
      ),
    )
  }

  return rows
})

const waitingForInputMessage = computed(() => {
  const endClientWaiting = isSupplierHire.value && !endClientInputComplete.value
  const workerWaiting =
    !workerCompletedAnswers.value || (workerHasFields.value && !workerCompletedFields.value)

  if (endClientWaiting && workerWaiting) {
    return $t('Waiting for input from the end client and the worker')
  }

  return endClientWaiting
    ? $t('Waiting for input from the end client')
    : $t('Waiting for input from the worker')
})

const canAcceptClassification = computed(
  () =>
    hasPendingAcceptanceClassification.value &&
    !currentClassification.value?.hasPendingApproval &&
    !currentClassification.value?.hasHireBlockingCondition,
)

const checkForAutoOpenModal = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const shouldOpenModal = urlParams.has('finalise_recommendation')

  if (shouldOpenModal && canAcceptClassification.value && currentClassification.value) {
    showConfirmationModal.value = true

    urlParams.delete('finalise_recommendation')
    const newUrl =
      window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '')
    window.history.replaceState({}, '', newUrl)
  }
}

watch(
  [currentClassification, canAcceptClassification],
  () => {
    if (currentClassification.value && !loading.value) {
      checkForAutoOpenModal()
    }
  },
  { immediate: true },
)

onMounted(() => {
  checkForAutoOpenModal()
})

// URL generation functions
const getEditUrl = () => {
  if (currentClassification.value) {
    // Remove any lingering query parameters
    router.replace({ path: route.path })

    const currentUrl = window.location.origin + route.fullPath
    return getClassificationEditUrl(currentClassification.value.type, hireNumber.value, currentUrl)
  }

  // If no classifications exist, we'll use a default type (usually US_WORKER_CLASSIFICATION)
  // This should be determined by the hire's context, but for now we'll use a default
  const currentUrl = window.location.origin + route.fullPath
  return getClassificationEditUrl('US_WORKER_CLASSIFICATION', hireNumber.value, currentUrl)
}

const getCreateUrl = () => {
  if (currentClassification.value) {
    const type = getClassificationTypeSlug(currentClassification.value.type)
    return `/hires/${hireNumber.value}/create-sds/${type}`
  }
  return '#'
}

const getContractTypeUrl = () => {
  const currentUrl = window.location.origin + route.fullPath
  const encodedRedirectUrl = encodeURIComponent(currentUrl)
  return `/hires/${hireNumber.value}/contract-type?redirect_uri=${encodedRedirectUrl}`
}

const { openCancelHireModal } = useCancelHireModal()
const { openTerminateContractModal } = useTerminateContractModal()

const determineCancelAction = () => {
  if (hire.value?.canCancelContract) {
    openCancelHireModal()
  } else {
    openTerminateContractModal()
  }
}

const handleAcceptClassification = () => {
  if (currentClassification.value) {
    showConfirmationModal.value = true
  }
}

const resetCurrentClassification = () => {
  showConfirmationModal.value = false
  showOverride.value = false
}

const handleCreateWCRSubmit = () => {
  isCreatingWCR.value = true
  const urlParams = new URLSearchParams(window.location.search)
  urlParams.set('finalise_recommendation', 'true')
  const newUrl = `${window.location.pathname}?${urlParams.toString()}`
  window.history.replaceState({}, '', newUrl)
}
</script>

<style scoped>
/* Tailwind preflight is off, so the details divider gets default borders on its other three sides. */
.classification-input-callout :deep(.wds-callout-details) {
  border-width: 0;
  border-top-width: 1px;
}
</style>
